import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import openaiService from '../openaiService';
import PDFViewer from './PDFViewer';

const HomePage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [chatInput, setChatInput] = useState('');
  const [chatContent, setChatContent] = useState(null);
  const [showChatResponse, setShowChatResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    '/asset/photo_sab.jpeg',
    '/asset/Kemeja BW.png',
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isLoading || isStreaming) return;

    const question = chatInput.trim();
    setIsLoading(true);
    setIsStreaming(true);
    setChatInput('');
    setChatContent({
      type: 'ai_response',
      question,
      answer: '',
      formattedAnswer: ''
    });
    setShowChatResponse(true);

    try {
      const response = await openaiService.askQuestion(question);
      setChatContent((prev) => ({
        ...prev,
        answer: response.raw,
        formattedAnswer: response.formatted
      }));
    } catch (error) {
      console.error('Error getting AI response:', error);
      setChatContent((prev) => ({
        ...prev,
        answer: 'Apologies, I’m unable to provide an answer to this question. Kindly submit another question or contact Sabtian directly.',
        formattedAnswer: 'Apologies, I’m unable to provide an answer to this question. Kindly submit another question or contact Sabtian directly.'
      }));
    } finally {
      setIsStreaming(false);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleChatSubmit(e);
    }
  };

  return (
    <div className="bg-gradient-to-r from-gray-200 via-slate-400 to-gray-900 h-screen w-full max-h-screen max-w-full overflow-hidden relative">
      {/* Navigation */}
      <nav className="absolute top-4 left-4 right-4 z-20 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowPDFViewer(true)}
            className="inline-flex items-center px-3 py-2 bg-black hover:bg-gray-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Resume
          </button>
          <Link
            to="/about"
            className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            About Me
          </Link>
          <button
            className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            onClick={() => {
              setChatContent({
                type: 'contacts',
                contacts: {
                  location: 'Jakarta, Indonesia',
                  phone: '+62 857 1630 0647',
                  email: 'sabtianadiansyah@gmail.com',
                  linkedin: 'linkedin.com/in/sabtianadiansyah',
                  github: 'github.com/sabtianady'
                }
              });
              setShowChatResponse(true);
            }}
          >
            Contacts
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <img
            src="/asset/logo_sab.png"
            alt="Sabtian Logo"
            className="w-12 h-12 sm:w-16 sm:h-16 md:w-12 md:h-12 rounded-full object-cover shadow-lg border-2 border-black"
          />
        </div>
      </nav>

      {/* Background Effects */}
      {[200, 150, 100].map((offset, idx) => (
        <div
          key={idx}
          className="absolute pointer-events-none transition-all duration-500 ease-out"
          style={{
            left: mousePosition.x - offset,
            top: mousePosition.y - offset,
            width: `${offset * 2}px`,
            height: `${offset * 2}px`,
            background: `radial-gradient(circle, rgba(${[59, 147, 16][idx]}, ${[130, 51, 185][idx]}, ${[246, 234, 129][idx]}, 0.0${idx + 1}) 0%, transparent 70%)`,
            borderRadius: '50%'
          }}
        />
      ))}

      {/* Main Content */}
      <main className="h-full flex items-center justify-center">
        <div className="text-black text-center px-4 sm:px-6 md:px-8 max-w-2xl w-full">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Sabtian Portfolio
          </h1>
          <section>
            <div className="mb-6 flex justify-center">
              <div className="relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-full overflow-hidden shadow-2xl border-4 border-black">
                <img
                  src={images[currentImageIndex]}
                  alt={`Sabtian portfolio photo ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover transition-opacity duration-3000"
                />
              </div>
            </div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-black-300 mb-8">
              Discover more about me. <br />
              Ask about my background or experience! <br />
              Your questions answered by AI. 🤖
            </p>
          </section>

          {/* Chat Form */}
          <form onSubmit={handleChatSubmit} className="mb-6 relative">
            <input
              id="chat-input"
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your question here... 💬"
              disabled={isLoading || isStreaming}
              className="w-full px-4 py-3 pr-12 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoading || isStreaming || !chatInput.trim()}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white p-2 rounded-md"
            >
              {(isLoading || isStreaming) ? '⌛' : '🚀'}
            </button>
          </form>
        </div>
      </main>

      {/* Chat Response Modal */}
      {showChatResponse && (
        <section>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-fade-in">
              <div className="p-6">
                {chatContent && chatContent.type === 'contacts' && (
  <div className="animate-fade-in">
    {/* Header */}
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-4 text-white mb-6">
      <h4 className="text-lg md:text-xl font-bold mb-2 flex items-center">
        <span className="mr-2">📞</span>
        Let's Connect!
      </h4>
      <p className="text-purple-100">
        I'm always open to discussing new opportunities, collaborations, or just having a tech chat!
      </p>
    </div>

    {/* Contact Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Location */}
      <div className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors animate-slide-in-left">
        <div className="flex items-center gap-3">
          <div className="text-2xl">📍</div>
          <div>
            <h5 className="text-white font-semibold">Location</h5>
            <p className="text-gray-300 text-sm">{chatContent.contacts.location}</p>
          </div>
        </div>
      </div>

      {/* Phone */}
<div className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors animate-slide-in-right">
  <div className="flex items-center gap-3">
    <div className="text-2xl">📱</div>
    <div>
      <h5 className="text-white font-semibold">Phone</h5>
      <a
        href={`https://wa.me/${chatContent.contacts.phone.replace(/\D/g, '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
      >
        {chatContent.contacts.phone}
      </a>
    </div>
  </div>
</div>

      {/* Email */}
<div className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors animate-slide-in-left" style={{ animationDelay: '0.1s' }}>
  <div className="flex items-center gap-3">
    <div className="text-2xl">📧</div>
    <div>
      <h5 className="text-white font-semibold">Email</h5>
      <a
        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${chatContent.contacts.email}&su=Let's Connect!&body=Hi Sabtian, I'd love to discuss...`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
      >
        {chatContent.contacts.email}
      </a>
    </div>
  </div>
</div>

      {/* LinkedIn */}
      <div className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors animate-slide-in-right" style={{ animationDelay: '0.1s' }}>
        <div className="flex items-center gap-3">
          <div className="text-2xl">💼</div>
          <div>
            <h5 className="text-white font-semibold">LinkedIn</h5>
            <a href={`https://${chatContent.contacts.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm transition-colors">
              {chatContent.contacts.linkedin}
            </a>
          </div>
        </div>
      </div>

      {/* GitHub */}
      <div className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors animate-slide-in-left md:col-span-2" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center gap-3">
          <div className="text-2xl">💻</div>
          <div>
            <h5 className="text-white font-semibold">GitHub</h5>
            <a href={`https://${chatContent.contacts.github}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm transition-colors">
              {chatContent.contacts.github}
            </a>
          </div>
        </div>
      </div>
    </div>

    {/* Call to Action */}
    <div className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
      <p className="text-white mb-3">Have questions about my portfolio or want to discuss potential opportunities? Let’s connect!</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a 
    href="https://mail.google.com/mail/?view=cm&fs=1&to=sabtianadiansyah@gmail.com&su=Let%27s%20Connect!&body=Hi%20Sabtian%2C%20I%27d%20love%20to%20discuss..."
    target="_blank"
    rel="noopener noreferrer"
    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
    aria-label="Send email via Gmail"
  >
    📧 Send via Gmail
  </a>
        <a
          href={`https://${chatContent.contacts.linkedin}`}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
        >
          💼 Connect on LinkedIn
        </a>
      </div>
    </div>

    {/* Bottom Close Button */}
    <div className="mt-6 flex justify-between items-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
      <span className="text-gray-400 text-sm font-medium">
        🤖
      </span>
      <button
        onClick={() => setShowChatResponse(false)}
        className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
      >
        ✕ Close
      </button>
    </div>
  </div>
)}
                {chatContent && chatContent.type === 'ai_response' && (
                  <div className="animate-fade-in">
                    {/* Question */}
                    <div className="bg-blue-600 rounded-lg p-4 text-white mb-4 animate-slide-in-right">
                      <div className="flex items-start gap-3">
                        <div className="text-xl flex-shrink-0 mt-0.5">❓</div>
                        <div>
                          <h5 className="font-semibold mb-2">Your Question:</h5>
                          <p className="text-blue-100 text-sm md:text-base leading-relaxed">
                            {chatContent.question}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* AI Response */}
                    <div className="bg-gray-800 rounded-lg p-4 text-white animate-slide-in-left">
                      <div className="flex items-start gap-3">
                        <div className="text-xl flex-shrink-0 mt-0.5">🤖</div>
                        <div className="flex-1">
                          <h5 className="font-semibold mb-2 text-green-400 flex items-center gap-2">
                            AI Assistant:
                            {isStreaming && (
                              <div className="flex items-center gap-1 ml-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                              </div>
                            )}
                          </h5>
                          <div
                            className="text-gray-200 text-sm md:text-base leading-relaxed prose prose-invert prose-sm md:prose-base max-w-none"
                            dangerouslySetInnerHTML={{ __html: chatContent.formattedAnswer || chatContent.answer }}
                          />
                          {isStreaming && !chatContent.answer && (
                            <div className="text-gray-400 italic text-sm flex items-center gap-2">
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Thinking...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Bottom Close Button */}
                    <div className="mt-6 flex justify-between items-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
                      <span className="text-gray-400 text-sm font-medium">
                        Supported by Deepseek
                      </span>
                      <button
                        onClick={() => setShowChatResponse(false)}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                      >
                        ✕ Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PDF Viewer Modal */}
      {showPDFViewer && (
        <PDFViewer
          pdfUrl="/asset/CV Sabtian Adiansyah.pdf"
          fileName="CV Sabtian Adiansyah.pdf"
          onClose={() => setShowPDFViewer(false)}
        />
      )}
    </div>
  );
};

export default HomePage;
